import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { parseParkedOrderName, truncateText } from '~Scenes/ParkedOrders/utils';
import { DateTime } from 'luxon';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import { Cheque } from '@baemingo/table-events/dist/types';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';

const ParkedOrder = styled(Button)<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 16px;
  text-transform: none;
  min-height: 64px;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const Column = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 96px;
  min-width: 96px;
  flex-shrink: 0;
`;

interface Props {
  order: Cheque;
  isSelected: boolean;
  onSelectOrder: (order: Cheque) => void;
}

/**
 * Component for displaying a single row in the parked orders list.
 * Each row shows general order details and is clickable.
 * When clicked, the selected order is displayed in the details panel on the right.
 *
 * If company has no staff - we display the deviceName it was parked on instead of the staff name.
 */
const ParkedOrderRow: FC<Props> = ({ order, isSelected, onSelectOrder }) => {
  const { i18n } = useI18n();

  const staff = useSelector((state) => state.companyStaff)?.staffs;
  const companyHasStaff = staff && staff?.length > 0;

  const currency = useCurrency();

  const parkedOrderButton = useButton({
    onClick: () => onSelectOrder(order),
  });

  const parsedOrder = parseParkedOrderName(order.name ?? '');

  /** Helper to get the correct copy for the date it was parked */
  const getDateCopy = (date: string) => {
    const orderDate = DateTime.fromISO(date);
    const today = DateTime.now().startOf('day');
    const yesterday = today.minus({ days: 1 });

    return {
      header: orderDate >= today ? i18n('ParkedOrders.ParkedAt') : i18n('ParkedOrders.Parked'),
      paragraph:
        orderDate >= today
          ? orderDate.toFormat('HH:mm')
          : orderDate >= yesterday
          ? i18n('General.Yesterday')
          : orderDate.toFormat('dd/MM'),
    };
  };

  const dateCopy = getDateCopy(order.createdAt);

  return (
    <ParkedOrder key={order.id} isSelected={isSelected} {...parkedOrderButton} padding="small">
      <Column display="flex" flexDirection="column" alignItems="flex-start">
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {i18n('ParkedOrders.Name')}
        </Heading>
        <Paragraph
          fontWeight={600}
          level={4}
          color={parsedOrder.orderName ? 'var(--colorWhite)' : 'var(--colorBlueGray100)'}>
          {parsedOrder.orderName
            ? truncateText(parsedOrder.orderName)
            : i18n('ParkedOrders.Untitled')}
        </Paragraph>
      </Column>

      <Column display="flex" flexDirection="column" alignItems="flex-start">
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {companyHasStaff ? i18n('ParkedOrders.Staff') : i18n('ParkedOrders.DeviceParkedOn')}
        </Heading>
        <Paragraph
          style={{ whiteSpace: 'nowrap' }}
          fontWeight={600}
          level={4}
          color="var(--colorWhite)">
          {companyHasStaff
            ? truncateText(
                staff?.find((s) => s.id === order.owner)?.name ?? i18n('ParkedOrders.NoStaff'),
              )
            : parsedOrder.deviceName}
        </Paragraph>
      </Column>

      <Column display="flex" flexDirection="column" alignItems="flex-start">
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {dateCopy.header}
        </Heading>
        <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
          {dateCopy.paragraph}
        </Paragraph>
      </Column>

      <Column display="flex" flexDirection="column" alignItems="flex-start">
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {i18n('POS.Total')}
        </Heading>
        <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
          {currency.format(order.amount)}
        </Paragraph>
      </Column>

      <Box display="flex" alignItems="center">
        <Icon
          src={ArrowForwardIcon}
          color={isSelected ? 'var(--colorWhite)' : 'var(--colorBlueGray1100)'}
        />
      </Box>
    </ParkedOrder>
  );
};

export default ParkedOrderRow;
